import { describe_error } from '../utils/ErrorDescriber';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner, Alert, Modal, Button, Form } from 'react-bootstrap';
import axios from 'axios';

import Constants from '../Constants';
import { useAuth } from '../AuthContext';
import { get_user_error_message } from '../utils/ErrorMessages';
import TrumbowygEditor from '../components/TrumbowygEditor';

import './css/help.css';

// The help page the side menu opens first, which also lists every page for the phone layout's page picker.
const c_IntroductionKey = 'introduction';

// The image types a help image may be; the backend accepts the same.
const c_ImageMimeTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

const c_NewSectionTitle = 'New Section';

// One help page, /help/:key: its title, image and sections. A superadmin can edit the page's title and image, edit
// each section's title, image and body, add a section and delete one. Every edit is checked again by the backend.
function HelpView(){
	const { key } = useParams();
	const { token, is_superadmin } = useAuth();
	const navigate = useNavigate();

	const [page, set_page] = useState(null);
	const [sections, set_sections] = useState([]);
	const [all_pages, set_all_pages] = useState([]);
	const [help_nav_open, set_help_nav_open] = useState(false);
	const [loading, set_loading] = useState(true);
	const [load_error, set_load_error] = useState('');

	const [editing_page, set_editing_page] = useState(false);
	const [page_title_draft, set_page_title_draft] = useState('');
	const [page_image_url_draft, set_page_image_url_draft] = useState(null);
	const [uploading_page_image, set_uploading_page_image] = useState(false);

	const [editing_section_id, set_editing_section_id] = useState(null);
	const [section_title_draft, set_section_title_draft] = useState('');
	const [section_image_url_draft, set_section_image_url_draft] = useState(null);
	const [uploading_section_image, set_uploading_section_image] = useState(false);

	const [save_error, set_save_error] = useState('');
	const [adding_section, set_adding_section] = useState(false);
	const [deleting_section_id, set_deleting_section_id] = useState(null);
	const [confirm_delete_section, set_confirm_delete_section] = useState(null);

	// The section image shown full size in a popup, { src, alt }, or null. The popup is not modal: a click anywhere
	// outside it, or Escape, closes it, and the page stays usable.
	const [enlarged_image, set_enlarged_image] = useState(null);

	const help_nav_ref = useRef(null);
	const editor_ref = useRef(null);
	const enlarged_image_ref = useRef(null);

	const get_headers = ()=>{
		return {
			Authorization: 'Bearer ' + token
		};
	};

	// A stored image_url is the backend's path for the image; the server's address goes in front of it.
	const get_image_src = (image_url)=>{
		return Constants.SERVER_URL + image_url;
	};

	// Loads the page whenever the address names another one. Only the key starts a load: a renewed login token must
	// not reload the page under an edit in progress.
	useEffect(()=>{
		const load_page = async()=>{
			set_loading(true);
			set_load_error('');
			set_editing_page(false);
			set_editing_section_id(null);
			set_save_error('');

			try {
				const sections_response = await axios.get(Constants.SERVER_URL + '/api/help_page_sections/' + encodeURIComponent(key), { headers: get_headers() });
				set_page(sections_response.data.page);
				set_sections(sections_response.data.sections);

				if(key === c_IntroductionKey){
					const pages_response = await axios.get(Constants.SERVER_URL + '/api/help_pages', { headers: get_headers() });
					set_all_pages(pages_response.data.pages);
				}
			} catch (error) {
				console.log('HelpView/load_page - ERROR: ', describe_error(error));
				set_page(null);
				set_sections([]);
				set_load_error(get_user_error_message(error, 'The help page could not be loaded.'));
			}

			set_loading(false);
		};

		load_page();
	}, [key]);

	// The phone layout's page picker closes on a click anywhere outside it.
	useEffect(()=>{
		const handle_click_outside = (event)=>{
			if(help_nav_ref.current && !help_nav_ref.current.contains(event.target)){
				set_help_nav_open(false);
			}
		};

		document.addEventListener('mousedown', handle_click_outside);
		return ()=>{
			document.removeEventListener('mousedown', handle_click_outside);
		};
	}, []);

	// While an image is enlarged, a click outside the popup or Escape closes it.
	useEffect(()=>{
		if(!enlarged_image){
			return;
		}

		const handle_mouse_down = (event)=>{
			if(enlarged_image_ref.current && !enlarged_image_ref.current.contains(event.target)){
				set_enlarged_image(null);
			}
		};

		const handle_key_down = (event)=>{
			if(event.key === 'Escape'){
				set_enlarged_image(null);
			}
		};

		document.addEventListener('mousedown', handle_mouse_down);
		document.addEventListener('keydown', handle_key_down);
		return ()=>{
			document.removeEventListener('mousedown', handle_mouse_down);
			document.removeEventListener('keydown', handle_key_down);
		};
	}, [enlarged_image]);

	const read_file_as_data_url = (file)=>{
		return new Promise((resolve, reject)=>{
			const reader = new FileReader();
			reader.onload = ()=>{
				resolve(reader.result);
			};
			reader.onerror = ()=>{
				reject(reader.error);
			};
			reader.readAsDataURL(file);
		});
	};

	const measure_image = (data_url)=>{
		return new Promise((resolve, reject)=>{
			const image = new window.Image();
			image.onload = ()=>{
				resolve({ width: image.naturalWidth, height: image.naturalHeight });
			};
			image.onerror = ()=>{
				reject(new Error('The image could not be read.'));
			};
			image.src = data_url;
		});
	};

	// Stores the image and returns the image_url the page or section shows it from.
	const upload_image = async(file)=>{
		const data_url = await read_file_as_data_url(file);
		const size = await measure_image(data_url);
		const image_base64 = data_url.slice(data_url.indexOf(',') + 1);

		const response = await axios.post(Constants.SERVER_URL + '/api/help_images', {
			mime_type: file.type,
			width: size.width,
			height: size.height,
			image_base64
		}, { headers: get_headers() });

		return response.data.image_url;
	};

	// The file input's change, for the page's image or a section's: set_uploading and set_image_url_draft are that
	// editor's.
	const handle_image_upload = async(event, set_uploading, set_image_url_draft)=>{
		const file = event.target.files[0];
		if(!file){
			return;
		}

		if(!c_ImageMimeTypes.includes(file.type)){
			set_save_error('Unsupported image format. Use PNG, JPEG, GIF or WebP.');
			return;
		}

		set_uploading(true);
		set_save_error('');

		try {
			const image_url = await upload_image(file);
			set_image_url_draft(image_url);
		} catch (error) {
			console.log('HelpView/handle_image_upload - ERROR: ', describe_error(error));
			set_save_error(get_user_error_message(error, 'The image could not be uploaded.'));
		}

		set_uploading(false);
	};

	const handle_edit_page = ()=>{
		set_save_error('');
		set_page_title_draft(page.title);
		set_page_image_url_draft(page.image_url);
		set_editing_page(true);
	};

	const handle_save_page = async()=>{
		set_save_error('');

		try {
			const response = await axios.put(Constants.SERVER_URL + '/api/help_page/' + page.id, {
				title: page_title_draft,
				image_url: page_image_url_draft
			}, { headers: get_headers() });

			set_page(response.data.page);
			set_editing_page(false);
		} catch (error) {
			console.log('HelpView/handle_save_page - ERROR: ', describe_error(error));
			set_save_error(get_user_error_message(error, 'The page could not be saved.'));
		}
	};

	// The sort order for a section placed after sections[index], or first when index is -1: the midpoint of the
	// sections it goes between, so no other section has to move.
	const get_new_section_sort_order = (index)=>{
		const previous_section = (index >= 0) ? sections[index] : null;
		const next_section = sections[index + 1];

		if(previous_section && next_section){
			return (previous_section.sort_order + next_section.sort_order) / 2;
		}

		if(previous_section){
			return previous_section.sort_order + 1;
		}

		if(next_section){
			return next_section.sort_order - 1;
		}

		return 1;
	};

	// Adds a section after sections[index] (first when index is -1) and opens it for editing.
	const handle_add_section = async(index)=>{
		set_save_error('');
		set_adding_section(true);

		try {
			const response = await axios.post(Constants.SERVER_URL + '/api/help_sections', {
				page_id: page.id,
				title: c_NewSectionTitle,
				sort_order: get_new_section_sort_order(index)
			}, { headers: get_headers() });

			const new_section = response.data.section;
			const updated_sections = sections.slice();
			updated_sections.splice(index + 1, 0, new_section);
			set_sections(updated_sections);

			set_section_title_draft(new_section.title);
			set_section_image_url_draft(new_section.image_url);
			set_editing_section_id(new_section.id);
		} catch (error) {
			console.log('HelpView/handle_add_section - ERROR: ', describe_error(error));
			set_save_error(get_user_error_message(error, 'The section could not be added.'));
		}

		set_adding_section(false);
	};

	const handle_delete_section = async(section)=>{
		set_save_error('');
		set_deleting_section_id(section.id);

		try {
			await axios.delete(Constants.SERVER_URL + '/api/help_section/' + section.id, { headers: get_headers() });
			set_sections(sections.filter((existing_section)=>{
				return existing_section.id !== section.id;
			}));
		} catch (error) {
			console.log('HelpView/handle_delete_section - ERROR: ', describe_error(error));
			set_save_error(get_user_error_message(error, 'The section could not be deleted.'));
		}

		set_deleting_section_id(null);
	};

	const handle_edit_section = (section)=>{
		set_save_error('');
		set_section_title_draft(section.title);
		set_section_image_url_draft(section.image_url);
		set_editing_section_id(section.id);
	};

	const handle_save_section = async(section)=>{
		set_save_error('');

		try {
			const response = await axios.put(Constants.SERVER_URL + '/api/help_section/' + section.id, {
				title: section_title_draft,
				body: editor_ref.current.get_html(),
				image_url: section_image_url_draft
			}, { headers: get_headers() });

			const saved_section = response.data.section;
			set_sections(sections.map((existing_section)=>{
				return (existing_section.id === saved_section.id) ? saved_section : existing_section;
			}));
			set_editing_section_id(null);
		} catch (error) {
			console.log('HelpView/handle_save_section - ERROR: ', describe_error(error));
			set_save_error(get_user_error_message(error, 'The section could not be saved.'));
		}
	};

	const handle_confirm_delete = ()=>{
		const section = confirm_delete_section;
		set_confirm_delete_section(null);
		handle_delete_section(section);
	};

	const render_delete_confirm_modal = ()=>{
		if(!confirm_delete_section){
			return null;
		}

		return (
			<Modal show size="sm" centered onHide={()=>{ set_confirm_delete_section(null); }}>
				<Modal.Header>
					<Modal.Title>Delete Section</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>Delete <strong>{confirm_delete_section.title}</strong>?</p>
					<p>This cannot be undone.</p>
				</Modal.Body>
				<Modal.Footer>
					<Button variant="outline-primary" onClick={()=>{ set_confirm_delete_section(null); }}>
						Cancel
					</Button>
					<Button variant="danger" onClick={handle_confirm_delete}>
						Delete
					</Button>
				</Modal.Footer>
			</Modal>
		);
	};

	// The enlarged section image, at its full size, over the page dimmed behind it; scrolls inside the popup when
	// larger than the window.
	const render_enlarged_image = ()=>{
		if(!enlarged_image){
			return null;
		}

		return (
			<React.Fragment>
				<div className="help-image-popup-dim"></div>
				<div className="help-image-popup" ref={enlarged_image_ref}>
					<img src={enlarged_image.src} alt={enlarged_image.alt} className="help-image-popup-image"/>
				</div>
			</React.Fragment>
		);
	};

	const render_loading = ()=>{
		return (
			<div className="help-loading">
				<Spinner animation="border" size="sm"/>
			</div>
		);
	};

	const render_load_error = ()=>{
		return (
			<Alert variant="danger">{load_error}</Alert>
		);
	};

	const render_save_error = ()=>{
		if(!save_error){
			return null;
		}

		return (
			<p className="help-save-error">{save_error}</p>
		);
	};

	// The image controls of an editor: the image's preview, a file input (a spinner while uploading) and, when
	// on_delete is given, a Delete Image button.
	const render_image_upload_row = (image_url_draft, uploading, on_change, on_delete)=>{
		return (
			<div className="help-image-upload-row">
				{image_url_draft ? (
					<img src={get_image_src(image_url_draft)} alt="Preview" className="help-image-preview"/>
				) : null}
				{uploading ? (
					<div className="help-image-uploading">
						<Spinner animation="border" size="sm"/>
						<span>Uploading...</span>
					</div>
				) : (
					<Form.Control
						type="file"
						accept={c_ImageMimeTypes.join(',')}
						className="help-image-upload-input"
						onChange={on_change}
					/>
				)}
				{on_delete ? (
					<Button variant="outline-danger" size="sm" className="help-delete-image-button" onClick={on_delete}>
						Delete Image
					</Button>
				) : null}
			</div>
		);
	};

	const render_edit_actions = (on_save, on_cancel)=>{
		return (
			<div className="help-edit-actions">
				<Button variant="primary" onClick={on_save}>Save</Button>
				<Button variant="outline-primary" onClick={on_cancel}>Cancel</Button>
			</div>
		);
	};

	const render_page_editor = ()=>{
		return (
			<div className="help-page-editor">
				<Form.Control
					type="text"
					className="help-edit-title-input"
					value={page_title_draft}
					onChange={(event)=>{ set_page_title_draft(event.target.value); }}
					onKeyDown={(event)=>{
						if(event.key === 'Enter'){
							handle_save_page();
						}
					}}
				/>
				{render_image_upload_row(
					page_image_url_draft,
					uploading_page_image,
					(event)=>{ handle_image_upload(event, set_uploading_page_image, set_page_image_url_draft); },
					null
				)}
				{render_save_error()}
				{render_edit_actions(handle_save_page, ()=>{ set_editing_page(false); })}
			</div>
		);
	};

	const render_page_title_row = ()=>{
		return (
			<div className="help-page-title-row">
				<h2 className="help-page-title">{page.title}</h2>
				{is_superadmin ? (
					<Button variant="outline-primary" size="sm" onClick={handle_edit_page}>Edit</Button>
				) : null}
			</div>
		);
	};

	// The phone layout's picker of the other help pages, shown on the introduction page, where the side menu is not.
	const render_mobile_nav = ()=>{
		const other_pages = all_pages.filter((help_page)=>{
			return help_page.key !== c_IntroductionKey;
		});

		if(other_pages.length === 0){
			return null;
		}

		return (
			<div className="help-mobile-nav d-lg-none" ref={help_nav_ref}>
				<div className="help-nav-trigger" onClick={()=>{ set_help_nav_open(!help_nav_open); }}>
					<span>Go to a help page...</span>
					<span className="help-nav-arrow">&#9660;</span>
				</div>
				{help_nav_open ? (
					<div className="help-nav-list">
						{other_pages.map((help_page)=>{
							return (
								<div
									key={help_page.id}
									className="help-nav-option"
									onClick={()=>{
										set_help_nav_open(false);
										navigate('/help/' + help_page.key);
									}}
								>
									{help_page.title}
								</div>
							);
						})}
					</div>
				) : null}
			</div>
		);
	};

	const render_section_admin_buttons = (section, index)=>{
		return (
			<React.Fragment>
				<Button variant="outline-primary" size="sm" onClick={()=>{ handle_edit_section(section); }}>
					Edit
				</Button>
				<Button variant="outline-primary" size="sm" disabled={adding_section} onClick={()=>{ handle_add_section(index); }}>
					{adding_section ? 'Adding...' : 'Add Section'}
				</Button>
				<Button
					variant="outline-danger"
					size="sm"
					className="help-delete-button"
					disabled={deleting_section_id === section.id}
					onClick={()=>{ set_confirm_delete_section(section); }}
				>
					{(deleting_section_id === section.id) ? 'Deleting...' : 'Delete'}
				</Button>
			</React.Fragment>
		);
	};

	// A section's image: the image, or a placeholder for a section that has never had one (image_url null). A
	// deleted image (an empty image_url) shows nothing.
	const render_section_image = (section)=>{
		if(section.image_url){
			const image_src = get_image_src(section.image_url);
			return (
				<img
					src={image_src}
					alt={section.title}
					className="help-section-image"
					onClick={()=>{ set_enlarged_image({ src: image_src, alt: section.title }); }}
				/>
			);
		}

		if(section.image_url === null){
			return (
				<div className="help-section-image-placeholder">
					<span>Image coming soon</span>
				</div>
			);
		}

		return null;
	};

	const render_section_view = (section, index)=>{
		return (
			<React.Fragment>
				<div className="help-section-title-row">
					<h3 className="help-section-title">{section.title}</h3>
					{is_superadmin ? render_section_admin_buttons(section, index) : null}
				</div>
				{render_section_image(section)}
				{section.body ? (
					<div className="help-section-body" dangerouslySetInnerHTML={{ __html: section.body }}/>
				) : null}
			</React.Fragment>
		);
	};

	const render_section_editor = (section)=>{
		return (
			<React.Fragment>
				<Form.Control
					type="text"
					className="help-edit-title-input"
					value={section_title_draft}
					onChange={(event)=>{ set_section_title_draft(event.target.value); }}
					onKeyDown={(event)=>{
						if(event.key === 'Enter'){
							handle_save_section(section);
						}
					}}
				/>
				{render_image_upload_row(
					section_image_url_draft,
					uploading_section_image,
					(event)=>{ handle_image_upload(event, set_uploading_section_image, set_section_image_url_draft); },
					()=>{ set_section_image_url_draft(''); }
				)}
				<TrumbowygEditor ref={editor_ref} initial_value={section.body}/>
				{render_save_error()}
				{render_edit_actions(()=>{ handle_save_section(section); }, ()=>{ set_editing_section_id(null); })}
			</React.Fragment>
		);
	};

	const render_section = (section, index)=>{
		return (
			<div key={section.id} className="help-section">
				{(editing_section_id === section.id) ? render_section_editor(section) : render_section_view(section, index)}
			</div>
		);
	};

	// A page with no sections yet: a superadmin can add the first.
	const render_no_sections = ()=>{
		return (
			<div className="help-empty">
				<p>No sections yet for this page.</p>
				{is_superadmin ? (
					<Button variant="outline-primary" size="sm" disabled={adding_section} onClick={()=>{ handle_add_section(-1); }}>
						{adding_section ? 'Adding...' : 'Add Section'}
					</Button>
				) : null}
			</div>
		);
	};

	const render_back_button = ()=>{
		if(page.key === c_IntroductionKey){
			return null;
		}

		return (
			<Button variant="primary" size="sm" className="d-lg-none help-back-button" onClick={()=>{ navigate('/help/' + c_IntroductionKey); }}>
				Back
			</Button>
		);
	};

	const render_content = ()=>{
		if(!page){
			return (
				<p className="help-empty">No content found for this page.</p>
			);
		}

		return (
			<React.Fragment>
				{render_back_button()}
				{editing_page ? render_page_editor() : render_page_title_row()}
				{(page.key === c_IntroductionKey) ? render_mobile_nav() : null}
				{(!editing_page && page.image_url) ? (
					<img src={get_image_src(page.image_url)} alt={page.title} className="help-page-image"/>
				) : null}
				{(!editing_page && !editing_section_id) ? render_save_error() : null}
				{(sections.length > 0) ? sections.map(render_section) : render_no_sections()}
			</React.Fragment>
		);
	};

	return (
		<React.Fragment>
			{render_delete_confirm_modal()}
			{render_enlarged_image()}
			<main className="view-container help-main">
				{loading ? render_loading() : null}
				{load_error ? render_load_error() : null}
				{(!loading && !load_error) ? render_content() : null}
			</main>
		</React.Fragment>
	);
}

export default HelpView;
